/**
 * The save file: what a child keeps between sessions.
 *
 * Deliberately tiny. Progress is per-level bests plus unlocks - not a snapshot
 * of the world. A mid-level save would have to serialise physics state, and the
 * moment the tuning changes it would restore a game that can no longer be won.
 * Levels are five minutes long; the correct granularity is the level.
 *
 * MIGRATION IS THE POINT OF THIS FILE. A save written by last week's build must
 * still load, because the alternative is a seven-year-old losing their stars
 * because an adult shipped an update. `migrate()` is the only way in, it never
 * throws, and anything it cannot understand becomes a fresh save rather than a
 * crash on boot.
 *
 * To add a schema change:
 *   1. Bump SAVE_VERSION.
 *   2. Add an entry to MIGRATIONS keyed by the version you are migrating FROM.
 *   3. Add a case to tests/unit/save.test.ts with a real old-format blob.
 * Never edit an existing migration - it has already run on real machines.
 *
 * Pure functions over plain data. The localStorage half lives in
 * src/engine/storage.ts, so this whole module is testable in Node.
 */

import type { BadgeId } from '@/systems/score';
import type { ScoredResult } from '@/systems/score';

export const SAVE_VERSION = 1;

export interface LevelRecord {
  levelId: string;
  /** Best total ever scored, badge bonuses included. */
  bestTotal: number;
  bestStars: number;
  /** Fastest completion in seconds. */
  bestTimeSeconds: number;
  /** Every badge ever earned on this level, across all attempts. */
  badges: BadgeId[];
  completed: boolean;
  plays: number;
}

export interface SaveData {
  version: number;
  levels: Record<string, LevelRecord>;
  /** Cozy Points accumulate across every play; they buy cosmetics (DESIGN.md 6). */
  cozyTotal: number;
}

export function createSave(): SaveData {
  return { version: SAVE_VERSION, levels: {}, cozyTotal: 0 };
}

export function createLevelRecord(levelId: string): LevelRecord {
  return {
    levelId,
    bestTotal: 0,
    bestStars: 0,
    bestTimeSeconds: Infinity,
    badges: [],
    completed: false,
    plays: 0,
  };
}

/**
 * Migration steps, keyed by the version being migrated FROM.
 *
 * Empty because version 1 is the first schema that ever shipped. It exists
 * anyway so the next person has an obvious, correct place to put the step
 * rather than inventing an ad-hoc upgrade path under time pressure - which is
 * how save systems eat people's progress.
 */
const MIGRATIONS: Record<number, (data: Record<string, unknown>) => Record<string, unknown>> = {};

/**
 * Turns anything at all into a valid SaveData. Never throws.
 *
 * Three cases, all real: a save from an older schema (walk it forward), a save
 * from a NEWER schema because the player opened an older build or a stale tab
 * (start fresh rather than silently dropping fields the new build wrote), and
 * garbage - truncated writes, a hand-edited file, a different game on the same
 * origin (start fresh).
 */
export function migrate(raw: unknown): SaveData {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return createSave();

  let data = { ...(raw as Record<string, unknown>) };
  let version = typeof data['version'] === 'number' ? (data['version'] as number) : 0;

  if (version > SAVE_VERSION) return createSave();

  while (version < SAVE_VERSION) {
    const step = MIGRATIONS[version];
    // No route from this version to the current one. Better a fresh save than a
    // half-migrated one that fails in some other way three screens later.
    if (!step) return createSave();
    data = step(data);
    version = typeof data['version'] === 'number' ? (data['version'] as number) : version + 1;
  }

  return repair(data);
}

/**
 * Field-by-field validation of a current-version save.
 *
 * Repairs rather than rejects: one corrupt level record should cost that level's
 * best score, not the whole file. Anything unreadable falls back to the value a
 * fresh save would have.
 */
function repair(data: Record<string, unknown>): SaveData {
  const save = createSave();
  save.cozyTotal = finiteOr(data['cozyTotal'], 0);

  const levels = data['levels'];
  if (typeof levels !== 'object' || levels === null || Array.isArray(levels)) return save;

  for (const [levelId, value] of Object.entries(levels as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) continue;
    const entry = value as Record<string, unknown>;
    const record = createLevelRecord(levelId);
    record.bestTotal = finiteOr(entry['bestTotal'], 0);
    record.bestStars = clampStars(finiteOr(entry['bestStars'], 0));
    // Infinity does not survive JSON, so a missing best time comes back as null.
    const time = entry['bestTimeSeconds'];
    record.bestTimeSeconds = typeof time === 'number' && Number.isFinite(time) ? time : Infinity;
    record.badges = Array.isArray(entry['badges'])
      ? (entry['badges'].filter((badge) => typeof badge === 'string') as BadgeId[])
      : [];
    record.completed = entry['completed'] === true;
    record.plays = Math.max(0, Math.floor(finiteOr(entry['plays'], 0)));
    save.levels[levelId] = record;
  }

  return save;
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clampStars(value: number): number {
  return Math.min(3, Math.max(0, Math.floor(value)));
}

/**
 * Folds a finished level into the save. Returns a NEW object; never mutates.
 *
 * Everything is a high-water mark, and badges accumulate across attempts rather
 * than being replaced by the latest run. A child who earned Ghost on Tuesday
 * must not lose it by playing loudly on Wednesday - the badge records something
 * they once did, and taking it back for a later, worse run is the single most
 * demoralising thing a progress system can do.
 */
export function recordResult(save: SaveData, scored: ScoredResult): SaveData {
  const levelId = scored.result.levelId;
  const previous = save.levels[levelId] ?? createLevelRecord(levelId);

  const record: LevelRecord = {
    levelId,
    bestTotal: Math.max(previous.bestTotal, scored.total),
    bestStars: Math.max(previous.bestStars, scored.stars),
    bestTimeSeconds: Math.min(previous.bestTimeSeconds, scored.result.elapsedSeconds),
    badges: [...new Set([...previous.badges, ...scored.earned])],
    completed: true,
    plays: previous.plays + 1,
  };

  return {
    version: SAVE_VERSION,
    cozyTotal: save.cozyTotal + scored.result.cozy,
    levels: { ...save.levels, [levelId]: record },
  };
}

/** Records an attempt that did not finish, so "plays" is honest. */
export function recordAttempt(save: SaveData, levelId: string): SaveData {
  const previous = save.levels[levelId] ?? createLevelRecord(levelId);
  return {
    ...save,
    levels: { ...save.levels, [levelId]: { ...previous, plays: previous.plays + 1 } },
  };
}

/** What the level card shows as "your best so far", or null on a first play. */
export function bestFor(save: SaveData, levelId: string): LevelRecord | null {
  return save.levels[levelId] ?? null;
}
